import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import BreadCrumb from 'jbc-front/components/BreadCrumb';
import ActionButton from 'jbc-front/components/ActionButton';
import { Edit, Revert, Download, MinusCircle, Strage } from 'jbc-front/components/icons';
import LoadingView from 'src/components/LoadingView';
import { FormErrors } from 'src/components';

import { REPORT_INDEX, INCOME_TAX_DECLARATION_LIST } from 'src/constants/EndpointUrls';
import { scrollToTop } from 'src/utils/Utils';

import MainDeclarationTab from './MainDeclarationTab';
import TaxAllocationAppendixTab from './TaxAllocationAppendixTab';
import DownloadDeclarationModal from './DownloadDeclarationModal';
import './DeclarationShowPage.scss';

const TABS = {
  MAIN: 'main',
  APPENDIX: 'appendix'
};

const DeclarationShowPage = ({
  declaration,
  branchAllocations,
  activeTab,
  isLoading,
  isLoadingAppendix,
  saveErrors,
  saveErrorFields,
  appendixSaveErrors,
  appendixSaveErrorFields,
  onSaveMain,
  onSaveAppendix,
  onTabChange,
  onClearErrors,
  onClearAppendixErrors,
  onReset,
  onDownload,
  dependentBranches,
  provinces,
  subdistricts,
  regionalTaxOffices,
  taxOffices
}) => {
  const { t } = useTranslation();
  const [isEditingMain, setIsEditingMain] = useState(false);
  const [isEditingAppendix, setIsEditingAppendix] = useState(false);
  const [draft, setDraft] = useState(null);
  const [allocationsDraft, setAllocationsDraft] = useState(null);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const hasAppendix = (declaration && declaration.ct15HasMultiProvinceAlloc) || (branchAllocations && branchAllocations.length > 0);

  const isEditing = activeTab === TABS.MAIN ? isEditingMain : isEditingAppendix;

  const ERROR_FIELD_MAP = {
    ct16_total_employees: { main: ['ct16TotalEmployees'], appendix: [] },
    ct17_resident_employees: { main: ['ct17ResidentEmployees'], appendix: [] },
    ct19_resident_with_tax: { main: ['ct19ResidentWithTax'], appendix: [] },
    ct20_non_resident_with_tax: { main: ['ct20NonResidentWithTax'], appendix: [] },
    ct22_resident_taxable_income: { main: ['ct22ResidentTaxableIncome'], appendix: [] },
    ct23_non_resident_taxable_income: { main: ['ct23NonResidentTaxableIncome'], appendix: [] },
    ct24_insurance_premium: { main: ['ct24InsurancePremium'], appendix: [] },
    ct25_oil_contract_exemption: { main: ['ct25OilContractExemption'], appendix: [] },
    ct27_resident_taxable_subject_to_withholding: { main: ['ct27ResidentTaxableSubjectToWithholding'], appendix: [] },
    ct28_non_resident_taxable_subject_to_withholding: { main: ['ct28NonResidentTaxableSubjectToWithholding'], appendix: [] },
    ct30_resident_tax: { main: ['ct30ResidentTax'], appendix: [] },
    ct31_non_resident_tax: { main: ['ct31NonResidentTax'], appendix: [] },
    ct12_employee_count: { main: [], appendix: ['ct12EmployeeCount'] },
    ct13_employees_with_tax: { main: [], appendix: ['ct13EmployeesWithTax'] },
    ct18_total_with_tax: {
      main: ['ct19ResidentWithTax', 'ct20NonResidentWithTax'],
      appendix: ['ct13EmployeesWithTax']
    },
    ct21_total_taxable_income: {
      main: ['ct22ResidentTaxableIncome', 'ct23NonResidentTaxableIncome'],
      appendix: ['ct10TaxableIncome']
    },
    ct26_total_taxable_subject_to_withholding: {
      main: ['ct27ResidentTaxableSubjectToWithholding', 'ct28NonResidentTaxableSubjectToWithholding'],
      appendix: ['ct11TaxableIncomeSubjectToWh']
    },
    ct29_total_tax_withheld: {
      main: ['ct30ResidentTax', 'ct31NonResidentTax'],
      appendix: ['ct14TaxWithheld']
    },
    province_code: { main: [], appendix: ['provinceId'] },
    district_code: { main: [], appendix: ['districtId'] },
    tax_office_code: { main: [], appendix: ['taxOfficeCode', 'regionalTaxOfficeCode'] },
    base: { main: [], appendix: ['clientBranchId'] }
  };

  const mainErrorFieldSet = useMemo(
    () => {
      const fields = new Set();
      if (saveErrorFields && saveErrorFields.length > 0) {
        saveErrorFields.forEach(key => {
          const mapping = ERROR_FIELD_MAP[key];
          if (mapping) {
            mapping.main.forEach(f => fields.add(f));
          }
        });
      }
      return fields;
    },
    [saveErrorFields]
  );

  const appendixErrorFieldSet = useMemo(
    () => {
      const fields = new Set();
      if (appendixSaveErrorFields && appendixSaveErrorFields.length > 0) {
        appendixSaveErrorFields.forEach(key => {
          const mapping = ERROR_FIELD_MAP[key];
          if (mapping) {
            mapping.appendix.forEach(f => fields.add(f));
          }
        });
      }
      return fields;
    },
    [appendixSaveErrorFields]
  );

  const toDraftValue = value => {
    if (value === null || value === undefined) return '';
    const num = Number(value);
    if (Number.isNaN(num)) return '';
    return String(Math.round(num));
  };

  const editableDraft = useMemo(
    () => {
      if (!declaration) return null;
      return {
        ct16TotalEmployees: toDraftValue(declaration.ct16TotalEmployees),
        ct17ResidentEmployees: toDraftValue(declaration.ct17ResidentEmployees),
        ct19ResidentWithTax: toDraftValue(declaration.ct19ResidentWithTax),
        ct20NonResidentWithTax: toDraftValue(declaration.ct20NonResidentWithTax),
        ct22ResidentTaxableIncome: toDraftValue(declaration.ct22ResidentTaxableIncome),
        ct23NonResidentTaxableIncome: toDraftValue(declaration.ct23NonResidentTaxableIncome),
        ct24InsurancePremium: toDraftValue(declaration.ct24InsurancePremium),
        ct25OilContractExemption: toDraftValue(declaration.ct25OilContractExemption),
        ct27ResidentTaxableSubjectToWithholding: toDraftValue(declaration.ct27ResidentTaxableSubjectToWithholding),
        ct28NonResidentTaxableSubjectToWithholding: toDraftValue(declaration.ct28NonResidentTaxableSubjectToWithholding),
        ct30ResidentTax: toDraftValue(declaration.ct30ResidentTax),
        ct31NonResidentTax: toDraftValue(declaration.ct31NonResidentTax)
      };
    },
    [declaration]
  );

  const editableAllocationsDraft = useMemo(
    () => {
      if (!branchAllocations || branchAllocations.length === 0) return [];
      return branchAllocations.map(alloc => {
        const isNewBranch = alloc.newBranch;
        return {
          id: alloc.id,
          ...(isNewBranch ? { tempId: `new_${alloc.clientBranchId}`, isNew: true } : {}),
          clientBranchId: alloc.clientBranchId,
          branchType: alloc.branchType,
          branchName: alloc.branchName,
          provinceId: alloc.provinceId ? String(alloc.provinceId) : '',
          provinceCode: alloc.provinceCode,
          provinceName: alloc.provinceName,
          districtId: alloc.districtId ? String(alloc.districtId) : '',
          districtCode: alloc.districtCode,
          districtName: alloc.districtName,
          taxOfficeCode: alloc.taxOfficeCode,
          taxOfficeName: alloc.taxOfficeName,
          parentTaxOfficeName: alloc.parentTaxOfficeName,
          taxCodeLocation: alloc.taxCodeLocation,
          regionalTaxOfficeCode: alloc.regionalTaxOfficeCode,
          ct10TaxableIncome: toDraftValue(alloc.ct10TaxableIncome),
          ct11TaxableIncomeSubjectToWh: toDraftValue(alloc.ct11TaxableIncomeSubjectToWh),
          ct12EmployeeCount: toDraftValue(alloc.ct12EmployeeCount),
          ct13EmployeesWithTax: toDraftValue(alloc.ct13EmployeesWithTax),
          ct14TaxWithheld: toDraftValue(alloc.ct14TaxWithheld)
        };
      });
    },
    [branchAllocations]
  );

  const startEditMain = () => {
    if (!declaration) return;
    setIsEditingMain(true);
    setDraft(editableDraft);
  };

  const startEditAppendix = () => {
    if (!declaration) return;
    setIsEditingAppendix(true);
    setAllocationsDraft(editableAllocationsDraft);
  };

  const cancelEditMain = () => {
    setIsEditingMain(false);
    setDraft(null);
    if (onClearErrors) onClearErrors();
  };

  const cancelEditAppendix = () => {
    setIsEditingAppendix(false);
    setAllocationsDraft(null);
    if (onClearAppendixErrors) onClearAppendixErrors();
  };

  const startEdit = () => {
    if (activeTab === TABS.MAIN) {
      startEditMain();
    } else {
      startEditAppendix();
    }
  };

  const cancelEdit = () => {
    if (activeTab === TABS.MAIN) {
      cancelEditMain();
    } else {
      cancelEditAppendix();
    }
  };

  const handleDraftChange = (field, value) => {
    setDraft(prev => ({ ...(prev || editableDraft), [field]: value }));
  };

  const handleAllocationDraftChange = (allocId, field, value) => {
    setAllocationsDraft(prev => {
      if (!prev) return prev;
      return prev.map(alloc => {
        if (alloc.id === allocId || alloc.tempId === allocId) {
          return { ...alloc, [field]: value };
        }
        return alloc;
      });
    });
  };

  const handleAllocationBatchUpdate = (allocId, updates) => {
    setAllocationsDraft(prev => {
      if (!prev) return prev;
      return prev.map(alloc => {
        if (alloc.id === allocId || alloc.tempId === allocId) {
          return { ...alloc, ...updates };
        }
        return alloc;
      });
    });
  };

  const handleAddAllocation = branch => {
    const tempId = `new_${Date.now()}`;
    // If branch is null, add an empty placeholder row
    const newAllocation = branch
      ? {
          tempId,
          clientBranchId: branch.id,
          branchType: 'branch',
          branchName: branch.name,
          provinceId: branch.provinceId ? String(branch.provinceId) : '',
          provinceName: branch.provinceName,
          districtId: branch.subdistrictId ? String(branch.subdistrictId) : '',
          districtName: branch.districtName,
          taxOfficeCode: branch.taxOfficeCode || '',
          taxOfficeName: branch.taxOfficeName,
          parentTaxOfficeName: branch.regionalTaxOfficeName,
          taxCodeLocation: branch.corporateNumber,
          regionalTaxOfficeCode: branch.regionalTaxOfficeCode || '',
          ct10TaxableIncome: '0',
          ct11TaxableIncomeSubjectToWh: '0',
          ct12EmployeeCount: '0',
          ct13EmployeesWithTax: '0',
          ct14TaxWithheld: '0',
          isNew: true
        }
      : {
          tempId,
          clientBranchId: null,
          branchType: 'branch',
          branchName: '',
          provinceId: '',
          provinceName: '',
          districtId: '',
          districtName: '',
          taxOfficeCode: '',
          taxOfficeName: '',
          parentTaxOfficeName: '',
          taxCodeLocation: '',
          regionalTaxOfficeCode: '',
          ct10TaxableIncome: '0',
          ct11TaxableIncomeSubjectToWh: '0',
          ct12EmployeeCount: '0',
          ct13EmployeesWithTax: '0',
          ct14TaxWithheld: '0',
          isNew: true,
          isEmpty: true // Flag to show branch selection dropdown
        };
    setAllocationsDraft(prev => [...(prev || []), newAllocation]);
  };

  const handleDeleteAllocation = allocId => {
    setAllocationsDraft(prev => {
      if (!prev) return prev;
      return prev
        .map(alloc => {
          if (alloc.id === allocId || alloc.tempId === allocId) {
            if (alloc.isNew) {
              return null;
            }
            return { ...alloc, destroy: true };
          }
          return alloc;
        })
        .filter(Boolean);
    });
  };

  const handleSave = async () => {
    if (activeTab === TABS.MAIN) {
      if (!onSaveMain || !draft) return;
      const ok = await onSaveMain(draft);
      if (ok) {
        setIsEditingMain(false);
        setDraft(null);
      } else {
        scrollToTop();
      }
    } else {
      if (!onSaveAppendix || !allocationsDraft) return;
      const ok = await onSaveAppendix(allocationsDraft);
      if (ok) {
        setIsEditingAppendix(false);
        setAllocationsDraft(null);
      } else {
        scrollToTop();
      }
    }
  };

  const handleTabChange = tab => {
    if (onTabChange) onTabChange(tab);
  };

  const buildTitle = () => {
    if (!declaration) return '';
    return declaration.declarationName || '';
  };

  const buildBranchDisplay = () => {
    if (!declaration) return '';
    return declaration.companyName || '';
  };

  const currentErrors = activeTab === TABS.MAIN ? saveErrors : appendixSaveErrors;
  const currentIsLoading = activeTab === TABS.MAIN ? isLoading : isLoadingAppendix;

  return (
    <div>
      <div className="l-wrap-xl l-contents-wrap declaration-show-page">
        {/* Breadcrumb */}
        <div className="u-mb20">
          <BreadCrumb>
            <BreadCrumb.Item label={t('features.Reports.Report.create_document')} path={REPORT_INDEX} />
            <BreadCrumb.Item label={t('features.ReportIncomeTax.title')} path={INCOME_TAX_DECLARATION_LIST} />
            <BreadCrumb.Item label={t('features.ReportIncomeTax.show.view_declaration')} />
          </BreadCrumb>
        </div>

        <LoadingView isLoading={currentIsLoading}>
          {!declaration ? (
            <div className="declaration-show-empty">{t('features.ReportIncomeTax.show.not_found')}</div>
          ) : (
            <>
              {/* Title */}
              <div className="declaration-show-header">
                <h1 className="m-title-main">{buildTitle()}</h1>
                <p className="declaration-show-branch">{buildBranchDisplay()}</p>
              </div>

              {/* Errors */}
              <FormErrors errors={currentErrors || []} title={t('features.ReportIncomeTax.show.save_error_title')} />

              {/* Step Tabs */}
              <div className="declaration-tabs">
                <div
                  className={`declaration-tab ${activeTab === TABS.MAIN ? 'declaration-tab--active' : ''}`}
                  onClick={() => handleTabChange(TABS.MAIN)}
                  role="button"
                  tabIndex={0}
                >
                  <span className="declaration-tab__title">{t('features.ReportIncomeTax.show.tabs.main_declaration')}</span>
                  <span className="declaration-tab__subtitle">{t('features.ReportIncomeTax.show.tabs.main_declaration_subtitle')}</span>
                </div>

                <div
                  className={`declaration-tab ${activeTab === TABS.APPENDIX ? 'declaration-tab--active' : ''}${
                    !hasAppendix ? ' declaration-tab--disabled' : ''
                  }`}
                  onClick={hasAppendix ? () => handleTabChange(TABS.APPENDIX) : undefined}
                  role="button"
                  tabIndex={hasAppendix ? 0 : -1}
                >
                  <span className="declaration-tab__title">{t('features.ReportIncomeTax.show.tabs.tax_allocation_appendix')}</span>
                  <span className="declaration-tab__subtitle">{t('features.ReportIncomeTax.show.tabs.tax_allocation_appendix_subtitle')}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="declaration-actions">
                <div className="declaration-actions__left">
                  {isEditing ? (
                    <>
                      <ActionButton onClick={handleSave} icon={<Strage size={15} />}>
                        {t('features.ReportIncomeTax.show.actions.save')}
                      </ActionButton>
                      <ActionButton onClick={cancelEdit} icon={<MinusCircle size={15} />}>
                        {t('features.ReportIncomeTax.show.actions.cancel')}
                      </ActionButton>
                    </>
                  ) : (
                    <>
                      <ActionButton onClick={onReset} icon={<Revert size={15} />}>
                        {t('features.ReportIncomeTax.show.actions.reset')}
                      </ActionButton>
                      <ActionButton onClick={startEdit} icon={<Edit size={15} />}>
                        {t('features.ReportIncomeTax.show.actions.edit')}
                      </ActionButton>
                    </>
                  )}
                </div>
                <div className="declaration-actions__right">
                  <ActionButton primary disabled={isEditing} onClick={() => setIsDownloadModalOpen(true)} icon={<Download size={15} />}>
                    {t('features.ReportIncomeTax.show.actions.download')}
                  </ActionButton>
                </div>
              </div>

              {/* Tab Content */}
              <div className="declaration-content">
                {activeTab === TABS.MAIN && (
                  <MainDeclarationTab
                    declaration={declaration}
                    isEditing={isEditingMain}
                    draft={draft}
                    onDraftChange={handleDraftChange}
                    errorFields={mainErrorFieldSet}
                  />
                )}
                {activeTab === TABS.APPENDIX &&
                  hasAppendix && (
                    <TaxAllocationAppendixTab
                      declaration={declaration}
                      branchAllocations={branchAllocations}
                      isEditing={isEditingAppendix}
                      allocationsDraft={allocationsDraft}
                      onAllocationDraftChange={handleAllocationDraftChange}
                      onAllocationBatchUpdate={handleAllocationBatchUpdate}
                      onAddAllocation={handleAddAllocation}
                      onDeleteAllocation={handleDeleteAllocation}
                      dependentBranches={dependentBranches}
                      provinces={provinces}
                      subdistricts={subdistricts}
                      regionalTaxOffices={regionalTaxOffices}
                      taxOffices={taxOffices}
                      errorFields={appendixErrorFieldSet}
                    />
                  )}
              </div>

              <DownloadDeclarationModal
                isOpen={isDownloadModalOpen}
                onCancel={() => setIsDownloadModalOpen(false)}
                onConfirm={async format => {
                  if (onDownload) {
                    const ok = await onDownload(declaration.id, format);
                    if (ok) setIsDownloadModalOpen(false);
                  }
                }}
              />
            </>
          )}
        </LoadingView>
      </div>
    </div>
  );
};

DeclarationShowPage.propTypes = {
  declaration: PropTypes.shape({
    id: PropTypes.number,
    clientBranchId: PropTypes.number,
    periodType: PropTypes.string,
    periodYear: PropTypes.number,
    periodMonth: PropTypes.number,
    periodQuarter: PropTypes.number,
    formCode: PropTypes.string,
    periodDisplay: PropTypes.string,
    supplementaryNo: PropTypes.number,
    branchName: PropTypes.string,
    branchDisplayName: PropTypes.string,
    companyName: PropTypes.string,
    companyTaxCode: PropTypes.string,
    ct15HasMultiProvinceAlloc: PropTypes.bool,
    ct16TotalEmployees: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct17ResidentEmployees: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct19ResidentWithTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct20NonResidentWithTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct22ResidentTaxableIncome: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct23NonResidentTaxableIncome: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct24InsurancePremium: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct25OilContractExemption: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct27ResidentTaxableSubjectToWithholding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct28NonResidentTaxableSubjectToWithholding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct30ResidentTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ct31NonResidentTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  branchAllocations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      clientBranchId: PropTypes.number,
      branchType: PropTypes.string,
      branchName: PropTypes.string,
      ct10TaxableIncome: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      ct11TaxableIncomeSubjectToWh: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      ct12EmployeeCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      ct13EmployeesWithTax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      ct14TaxWithheld: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  ),
  activeTab: PropTypes.oneOf(['main', 'appendix']),
  isLoading: PropTypes.bool,
  isLoadingAppendix: PropTypes.bool,
  saveErrors: PropTypes.arrayOf(PropTypes.string),
  saveErrorFields: PropTypes.arrayOf(PropTypes.string),
  appendixSaveErrors: PropTypes.arrayOf(PropTypes.string),
  appendixSaveErrorFields: PropTypes.arrayOf(PropTypes.string),
  onSaveMain: PropTypes.func.isRequired,
  onSaveAppendix: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onClearErrors: PropTypes.func,
  onClearAppendixErrors: PropTypes.func,
  onReset: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  dependentBranches: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      provinceId: PropTypes.number,
      provinceName: PropTypes.string
    })
  ),
  provinces: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string, labelEn: PropTypes.string })),
  subdistricts: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string }))),
  regionalTaxOffices: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({ taxOfficeCode: PropTypes.string, label: PropTypes.string }))),
  taxOffices: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({ taxOfficeCode: PropTypes.string, label: PropTypes.string })))
};

DeclarationShowPage.defaultProps = {
  declaration: null,
  branchAllocations: [],
  activeTab: 'main',
  isLoading: false,
  isLoadingAppendix: false,
  saveErrors: [],
  saveErrorFields: [],
  appendixSaveErrors: [],
  appendixSaveErrorFields: [],
  onDownload: null,
  onClearErrors: null,
  onClearAppendixErrors: null,
  dependentBranches: [],
  provinces: [],
  subdistricts: {},
  regionalTaxOffices: {},
  taxOffices: {}
};

export default DeclarationShowPage;
